// src/services/auth.ts

import { AuthTokens } from '../types';
import { api } from '../api';

// Единый in-flight refresh на уровне модуля: параллельные вызовы (axios-интерсептор,
// AuthGuard, несколько 401 одновременно) должны разделять ОДИН сетевой запрос,
// иначе они гоняются за ротацией refresh-токена на бэкенде (старый токен блокируется)
// и второй запрос получает 401 → пользователя выкидывает из аккаунта.
let inFlightRefresh: Promise<AuthTokens> | null = null;

export class AuthService {
  private tokensKey = 'tokens'; // Теперь храним все токены в одном ключе

  getTokens = (): AuthTokens | null => {
    const tokensStr = localStorage.getItem(this.tokensKey);
    if (!tokensStr) return null;
    try {
      return JSON.parse(tokensStr);
    } catch {
      return null;
    }
  };

  getAccessToken = (): string | null => {
    const tokens = this.getTokens();
    return tokens?.access_token || null;
  };

  getRefreshToken = (): string | null => {
    const tokens = this.getTokens();
    return tokens?.refresh_token || null;
  };

  setTokens = (tokens: AuthTokens) => {
    localStorage.setItem(this.tokensKey, JSON.stringify(tokens));
  };

  clearTokens = () => {
    localStorage.removeItem(this.tokensKey);
  };

  refreshTokens = async (): Promise<AuthTokens> => {
    // Если refresh уже выполняется — переиспользуем тот же запрос,
    // чтобы не отправлять на бэкенд уже заблокированный (ротированный) токен.
    if (inFlightRefresh) return inFlightRefresh;

    const tokens = this.getTokens();
    if (!tokens?.refresh_token) throw new Error('No refresh token');

    inFlightRefresh = (async () => {
      try {
        const response = await api.auth.refresh(tokens.refresh_token);
        this.setTokens(response.tokens);
        return response.tokens;
      } catch (error: any) {
        // Стираем токены только если бэкенд явно отверг refresh-токен (401/403).
        // При сетевых ошибках или 5xx (например, во время редеплоя) токены
        // сохраняем — следующая попытка refresh может пройти успешно.
        const status = error?.response?.status;
        if (status === 401 || status === 403) {
          this.clearTokens();
        }
        throw error;
      } finally {
        inFlightRefresh = null;
      }
    })();

    return inFlightRefresh;
  };

  isTokenExpired = (token: string): boolean => {
    if (!token) return true;
    
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expirationTime = payload.exp * 1000; // Convert to milliseconds
      return Date.now() >= expirationTime;
    } catch {
      return true;
    }
  };

  isAccessTokenExpired = (): boolean => {
    const tokens = this.getTokens();
    if (!tokens?.access_token) return true;
    
    try {
      const expiresAt = new Date(tokens.expires_at).getTime();
      return Date.now() >= expiresAt;
    } catch {
      return this.isTokenExpired(tokens.access_token);
    }
  };

  isRefreshTokenExpired = (): boolean => {
    const tokens = this.getTokens();
    return !tokens?.refresh_token || this.isTokenExpired(tokens.refresh_token);
  };
}

export const authService = new AuthService();
